//myutms/src/components/FormSettings/FormSubmitSettings/DisplayTableSubmitSettings.jsx

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { useFormSubmissionSettings } from '../../../contexts/FormSubmissionSettingsContext';
import ColumnDraftManager from './ColumnDraftManager';
import BitlyShortlinkSettingsSection from './BitlyShortlinkSettingsSection';

/* =============================================================================
 * DisplayTableSubmitSettings
 * -----------------------------------------------------------------------------
 * BASIC    – title / description / error + sticky-header
 * COLUMNS  – column visibility / order / calculated-field workflow
 * ADVANCED – integrations and provider-output behavior, starting with Bitly
 *
 * Enterprise UX note:
 * - Display Table uses a local 3-tab tabset instead of changing global CustomTabs.
 * - This keeps the Display Table sidepanel-specific UX isolated and prevents
 *   other sidepanel views from inheriting the Columns tab.
 * - Styling intentionally mirrors CustomTabs.jsx for visual parity.
 * ============================================================================= */

/* ─────────────────────────────────────────────────────────────────────────────
 * Display Table-specific tabs
 * -----------------------------------------------------------------------------
 * This is intentionally local to DisplayTableSubmitSettings.
 * Do not modify shared CustomTabs.jsx just to support this 3-tab use case,
 * because shared CustomTabs is used by other sidepanel settings surfaces.
 * ─────────────────────────────────────────────────────────────────────────── */
const DisplayTableTabsRoot = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: 'transparent', // Hide the default indicator line
  },
  backgroundColor: theme.palette.background.paper,
  paddingLeft: theme.spacing(2), // Add spacing on the left side
  '& .MuiTabs-flexContainer': {
    position: 'relative', // Needed to position the pseudoelement
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: '10%',
      borderBottom: '1px solid #dee2e6', // Continuous line across the tabs
      zIndex: 0,
    },
  },
}));

const DisplayTableTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: '0.75rem', // Smaller font size for the tabs
  color: '#555',
  marginRight: '0', // Remove space between tabs
  padding: theme.spacing(0.5, 1),
  minHeight: '42px', // Shorter tab height
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold, // Bold text for selected tab
    backgroundColor: '#fff', // White background for the selected tab
    borderBottom: '1px solid transparent', // Remove the bottom border
    zIndex: 2, // Ensure the tab is above the pseudo-element line
  },
  '&:not(.Mui-selected)': {
    backgroundColor: 'rgba(224, 224, 224, 0.5)', // Subtler gray background for unselected tabs
    borderBottom: '1px solid #dee2e6', // Visible bottom border for unselected tabs
  },
  '&:hover': {
    //backgroundColor: 'rgba(224, 242, 255, 0.5)', // Light blue background on hover
    opacity: 1,
  },
  borderRadius: '4px 4px 0 0',
  border: '1px solid #dee2e6',
  borderBottom: 'none', // Merge seamlessly with the content area below
}));

const DisplayTableSettingsTabs = ({ value, onChange }) => {
  return (
    <Box sx={{ bgcolor: 'background.paper' }}>
      <DisplayTableTabsRoot
        value={value}
        onChange={onChange}
        aria-label="display table submission settings"
      >
        <DisplayTableTab label="Basic" value="basic" />
        <DisplayTableTab label="Columns" value="columns" />
        <DisplayTableTab label="Advanced" value="advanced" />
      </DisplayTableTabsRoot>
    </Box>
  );
};

const BasicSettings = () => {
  /* 1️⃣ context values */
  const { submissionSettings, updateSettings } = useFormSubmissionSettings();
  const {
    /* texts */
    cardTitle:       ctxTitle       = 'Submission Saved',
    cardDescription: ctxDescription = 'Your entry was added to the table.',
    errorMessage:    ctxErrorMsg    = 'Not a valid submission. Please fill out all required fields.',
    /* toggles */
    enableStickyHeader: ctxSticky = false,
  } = submissionSettings?.addToSubmissionsTable || {};

  /* 2️⃣ local state mirrors context */
  const [tableTitle,       setTableTitle]       = useState(ctxTitle);
  const [tableDescription, setTableDescription] = useState(ctxDescription);
  const [errorMessage,     setErrorMessage]     = useState(ctxErrorMsg);
  const [enableStickyHeader, setEnableStickyHeader] = useState(ctxSticky);

  /* 3️⃣ keep in sync if context changes elsewhere */
  useEffect(() => {
    setTableTitle(ctxTitle);
    setTableDescription(ctxDescription);
    setErrorMessage(ctxErrorMsg);
    setEnableStickyHeader(ctxSticky);
  }, [ctxTitle, ctxDescription, ctxErrorMsg, ctxSticky]);

  /* 4️⃣ blur-only saves for text fields */
  const handleTitleBlur = useCallback(
    () => updateSettings('addToSubmissionsTable', { cardTitle: tableTitle }),
    [tableTitle, updateSettings]
  );

  const handleDescBlur = useCallback(
    () => updateSettings('addToSubmissionsTable', { cardDescription: tableDescription }),
    [tableDescription, updateSettings]
  );

  const handleErrorBlur = useCallback(
    () => updateSettings('addToSubmissionsTable', { errorMessage }),
    [errorMessage, updateSettings]
  );

  /* sticky-header toggle save */
  const handleToggleSticky = useCallback(
    (e) => {
      const newVal = e.target.checked;
      setEnableStickyHeader(newVal);
      updateSettings('addToSubmissionsTable', { enableStickyHeader: newVal });
    },
    [updateSettings]
  );

  /* 5️⃣ render */
  return (
    <Box sx={{ mt: 3 }}>
      {/* title / description / error */}
      <TextField
        variant="outlined"
        fullWidth
        value={tableTitle}
        onChange={(e) => setTableTitle(e.target.value)}
        onBlur={handleTitleBlur}
        label="Table Title"
        sx={{ mb: 2 }}
      />

      <TextField
        variant="outlined"
        fullWidth
        multiline
        minRows={3}
        value={tableDescription}
        onChange={(e) => setTableDescription(e.target.value)}
        onBlur={handleDescBlur}
        label="Table Description"
        sx={{ mb: 2 }}
      />

      <TextField
        variant="outlined"
        fullWidth
        multiline
        minRows={2}
        value={errorMessage}
        onChange={(e) => setErrorMessage(e.target.value)}
        onBlur={handleErrorBlur}
        label="Error Message"
        sx={{ mb: 3 }}
      />

      {/* sticky-header option  (moved from Advanced) */}
      <FormControlLabel
        control={
          <Switch
            checked={enableStickyHeader}
            onChange={handleToggleSticky}
            size="small"
          />
        }
        label={<Typography variant="body2">Enable Sticky Table Header</Typography>}
        sx={{ display: 'flex', alignItems: 'center', ml: 0, mb: 3 }}
      />
    </Box>
  );
};

/* ------------------------------------------------------------------------- */

const ColumnsSettings = () => {
  /* 1️⃣ context */
  const {
    submissionSettings,
    updateTableSurfaceSettings,
  } = useFormSubmissionSettings();
  const {
    columnOrder: ctxColumnOrder = [],
  } = submissionSettings?.addToSubmissionsTable || {};

  /* 2️⃣ render – column manager only */
  return (
    <Box sx={{ mt: 1 }}>
      <ColumnDraftManager
        columnOrder={ctxColumnOrder}
        settingsKey="addToSubmissionsTable"
        onChange={(updatedOrder) => {
          updateTableSurfaceSettings('addToSubmissionsTable', {
            columnOrder: updatedOrder,
          });
        }}
      />
    </Box>
  );
};

/* ------------------------------------------------------------------------- */

const AdvancedSettings = () => {
  /**
   * Display Table integration settings.
   *
   * Enterprise note:
   * - The Bitly integration lives in Advanced for parity with Display Card.
   * - The selected Display Table URL source is usually a calculated field.
   * - Bitly credential/OAuth state is still managed centrally from /integrations.
   * - The integration remains backend-owned; this UI only stages form-level config
   *   and manages provider-output column visibility.
   * - Future integrations such as HubSpot, Slack, BigQuery, and Bitly metrics can
   *   use this same Advanced tab pattern without cluttering Basic or Columns.
   */
  return (
    <Box sx={{ mt: 3 }}>
      <BitlyShortlinkSettingsSection
        actionType="addToSubmissionsTable"
        generatedOutputLabel="selected Display Table URL field"
      />
    </Box>
  );
};

/* ------------------------------------------------------------------------- */

const DisplayTableSubmitSettings = () => {
  const [activeTab, setActiveTab] = useState('basic');

  const handleChangeTab = useCallback((_e, newVal) => {
    setActiveTab(newVal);
    console.log('[DisplayTableSubmitSettings] Switched to tab:', newVal);
  }, []);

  return (
    <Box>
      <DisplayTableSettingsTabs value={activeTab} onChange={handleChangeTab} />
      {activeTab === 'basic'    && <BasicSettings   />}
      {activeTab === 'columns'  && <ColumnsSettings />}
      {activeTab === 'advanced' && <AdvancedSettings />}
    </Box>
  );
};

export default DisplayTableSubmitSettings;
